// Angular
import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

// FullCalendar
import { CalendarOptions, DateSelectArg, EventApi } from "@fullcalendar/core";
import timeGridPlugin from "@fullcalendar/timegrid";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import rrulePlugin from "@fullcalendar/rrule";
import heLocale from "@fullcalendar/core/locales/he";
import enLocale from "@fullcalendar/core/locales/en-gb";
import itLocale from "@fullcalendar/core/locales/it";
import esLocale from "@fullcalendar/core/locales/es";
import plLocale from "@fullcalendar/core/locales/pl";

// Third-party
import moment from "moment";
import { Subscription, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";
import * as momentTZ from "moment-timezone";

// Services
import { BlockService } from "./../block.service";
import { LoginService } from "../login.service";
import { AppointmentsService } from "../appointments.service";
import { PatientsService } from "../patients.service";
import { Patient } from "../patients/patient";
import { SettingsService } from "../settings.service";
import { AllDayEventService } from "../all-day-event.service";
import { environment } from "../../environments/environment";
import { MatCalendar } from "@angular/material/datepicker";
import { FullCalendarComponent } from "@fullcalendar/angular";
import { stringify } from "querystring";

@Component({
    selector: "app-appointments-general",
    templateUrl: "./appointments-general.component.html",
    styleUrls: ["./appointments-general.component.scss"],
    standalone: false
})
export class AppointmentsGeneralComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("fullcalendar") fullcalendarComponent!: FullCalendarComponent;
  @ViewChild("fullcalendar", { read: ElementRef })
  fullcalendarEl!: ElementRef<HTMLElement>;
  @ViewChild("resizableCalendar") resizableCalendar: ElementRef;
  @ViewChild("sideCalendar", { static: false }) sideCalendar: MatCalendar<moment.Moment>;
  @ViewChild("timeHoverRef") timeHoverRef!: ElementRef<HTMLDivElement>;

  showPage$: Subject<string> = new Subject<string>();
  events = [];
  calenderFormattedEvents = [];
  blocks = [];
  calenderFormattedBlocks = [];
  allDayEvents = [];
  calenderFormattedAllDayEvents = [];
  resources = [];
  calenderFormattedResources = [];
  showCalendar = false;
  showErrorModal = false;
  showExamErrorModal = false;
  examErrorName: string = "";
  showAllDayModal = false;
  showSelection = false;
  showBlock = false;
  showTransferAppointments = false;
  transferSourceDate: Date = null;
  transferSourceAppointments: any[] = [];
  transferResourceLabel = "";
  transferResourceId: string | number = null;
  selectedDateInfo;
  selectedEventInfo;
  page = "Calendar";
  examsLegend = {};
  holidays = [];
  holidayFetchStatus: { [year: number]: "fetching" | "fetched" | "error" } = {};
  ignoreFirstDatesSetTrigger = false;
  startDateDisplay;
  endDateDisplay;
  startDateDisplayWithGap;
  endDateDisplayWithGap;
  businessHours = false;
  console = console;
  eventHover = false;
  tooltipText = "";
  tooltipHtml: SafeHtml = "";
  tooltipPosition = { top: "auto", bottom: "auto", left: "auto", right: "auto", maxHeight: "80vh" };
  showAlert = false;
  viewType;
  switchDocMode = false;
  lmpDate; // LMP date in yyyy-mm-dd format
  appointmentsByRooms = false;
  roomHours = [];
  showSearch = false;
  slotDuration = "";
  slotMaxTime = "23:59:00";
  eventsByDate = {};
  openDocumentsAdd: boolean = false;
  isMultiSelect: boolean = false;
  selectedRooms: string[] = [];
  selectedDoctors: string[] = [];
  collapsed: boolean = false;
  private tooltipHideTimer: any;
  private hoverDebounceTimer: any;
  private currentHoverId?: string | number;
  minLMP: Date;
  maxLMP: Date;
  maxBD: Date;
  invalidlmp = "";

  calendarOptions: CalendarOptions = {
    schedulerLicenseKey: environment.fullCalendarLicenseKey,
    headerToolbar: {
      left: this.ls.premissions && this.ls.premissions?.multiSelect ? "toggleResourceView" : "",
      center: "title",
      right: "prev timeGridDay timeGridWeek dayGridMonth next myTodayButton",
    },
    resourceLabelContent: this.renderResourceLabel.bind(this),
    customButtons: this.getCustomButtons(),
    hiddenDays: [
      this.ls.premissions?.numberOfDaysInWeek ? Number(this.ls.premissions?.numberOfDaysInWeek) : 6,
    ],
    initialView: this.ls.premissions?.calendarInitialView
      ? this.ls.premissions?.calendarInitialView
      : "timeGridDay",
    nowIndicator: true,
    weekends: true,
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimeGridPlugin, rrulePlugin],
    editable: true,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    allDaySlot: true,
    slotMinTime: "07:00:00",
    slotMaxTime: this.slotMaxTime,
    slotDuration: this.ls.premissions?.calendarTimeSlotDuration,
    height: "auto", // Makes calendar adapt inside .calendar-container
    stickyHeaderDates: true,
    contentHeight: "100%", // Ensures it shrinks dynamically
    scrollTime: "07:00:00", // Ensures the view starts at 07:00
    eventMouseEnter: this.handleEventMouseEnter.bind(this),
    eventMouseLeave: this.handleEventMouseLeave.bind(this),
    datesSet: this.handleDatesSet.bind(this),
    businessHours: this.businessHours,
    select: this.handleDateSelect.bind(this),
    eventClick: this.handleEventClick.bind(this),
    eventsSet: this.handleEvents.bind(this),
    events: this.calenderFormattedEvents,
    resources: this.calenderFormattedResources,
    locales: [heLocale, enLocale, itLocale, esLocale, plLocale],
    locale: "en-gb", // Default locale
    firstDay: 0,
    navLinks: true,
    eventContent: this.renderEventContent.bind(this),
    eventDrop: this.handleEventDrop.bind(this),
    views: {
      dayGridMonth: {
        titleFormat: { year: "numeric", month: "long" },
      },
      timeGridWeek: {
        titleFormat: "",
        dayHeaderFormat: {
          weekday: "long",
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        },
      },
      timeGridDay: {
        dayHeaderFormat: {
          weekday: "long",
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        },
      },
    },
    slotLabelFormat: {
      hour: "2-digit",
      minute: "2-digit",
    },
    eventDidMount: this.eventDidMount.bind(this),
    dayHeaderContent: this.renderDayHeader.bind(this),
    dayCellContent: this.renderDayCell.bind(this),
  };
  currentEvents: EventApi[] = [];
  calendarVisible = true;
  showAppointment = false;
  showSelectPatient = false;
  showPatDetails = false;
  showPat = false;
  settingSub: Subscription;
  showPrintIcon: boolean = false;
  code = null;
  resourceId: any = null;
  private destroy$ = new Subject();
  private langChangeSubscription: Subscription;
  sideCalendarDate;
  yTime = "";
  examsCache: any[] = [];
  selectedExamId: string = "";
  selectedExamLength: number = 0;
  selectedExamName: string = "";

  error = "";
  sent = false;
  smsDisabled = false;
  creatingPending = false;
  smsPending = false;
  askSendSMS: boolean;
  sendUpdateSMS: boolean = false;
  appointmentAutoIndex;
  patientPID;

  constructor(
    public ls: LoginService,
    public appointmentService: AppointmentsService,
    public patientsService: PatientsService,
    public settings: SettingsService,
    private router: Router,
    public BlockService: BlockService,
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
    public allDayEventService: AllDayEventService,
    private sanitizer: DomSanitizer
  ) {
    this.maxLMP = new Date();
    this.maxLMP.setMonth(this.maxLMP.getMonth() - 1);
    this.minLMP = new Date();
    this.minLMP.setMonth(this.minLMP.getMonth() - 10);
    this.maxBD = new Date();
    this.maxBD.setFullYear(this.maxBD.getFullYear() - 12);
  }

  ngOnInit() {
    this.ls.mainScreenLoaded$.next();
    document.addEventListener("click", this.handleTransferIconClick, true);

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const patientId = params['pid'];
  
      if (patientId) {
        this.patientsService.getPatient(patientId, () => {
          this.showPat = true;
          this.page = 'Patients';
          this.showPage$.next('Patients');
          this.loadData();
        });
      } else {
        this.loadData();
      }
    });

    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      if (event.lang !== this.calendarOptions.locale) {
        this.changeLocale(event.lang);
      }
    });

    if (this.ls.ClinicID) {
      this.appointmentService.openExamCompletedSocket(this.ls.ClinicID);
    }

    this.appointmentService.examStatusIncomming.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
      this.appointmentsByRooms ? this.refetchRoomsActivityHours() : this.refetchActivityHours();
    });

    this.settings.examsSettingsLoaded$.subscribe((exams) => {
      this.examsCache = Array.isArray(exams) ? exams : [];
    });
  }

  eventDidMount(info) {
    if (info.event.extendedProps.background) {
      info.el.style.border = "4px solid " + info.event.extendedProps.background;
      info.el.style.opacity = "revert-layer";
      info.el.style.backgroundColor = "rgb(117 118 118)";
    }
  }

  calculatePregnancyAge() {
    const api = this.fullcalendarComponent?.getApi?.();
    if (!api) return;

    // remember current view/date/scroll
    const viewType = api.view.type;
    const date = api.getDate();

    const scroller = this.fullcalendarEl?.nativeElement.querySelector(
      ".fc-scroller, .fc-timegrid-body, .fc-timegrid-slots"
    ) as HTMLElement | null;
    const scrollTop = scroller?.scrollTop ?? 0;

    // trigger a re-render without changing where the user is
    api.batchRendering(() => {
      api.changeView(viewType); // changing to the same view forces header re-render
      api.gotoDate(date);
    });

    // restore scroll
    queueMicrotask(() => {
      if (scroller) scroller.scrollTop = scrollTop;
    });
  }

  renderResourceLabel(arg) {
    const resourceName = arg.resource.title;
    const icon = this.transferIconHtml(arg.date || arg.view?.currentStart, arg.resource?.id);

    if (arg.date) {
      const dateStr = arg.date.toLocaleDateString(this.calendarOptions.locale, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      });

      if (arg.resource?.id === this.calenderFormattedResources?.[0]?.id) {
        return {
          html: `<div style="display: flex; flex-direction: column;">
            <span>${resourceName}${icon}</span>
          </div>`,
        };
      } else {
        return {
          html: `<div style="display: flex; flex-direction: column;">
            <span>${resourceName}${icon}</span>
            <small>${dateStr}</small>
          </div>`,
        };
      }
    }

    return {
      html: `<div style="display: flex; flex-direction: column;">
        <span>${resourceName}${icon}</span>
      </div>`,
    };
  }

  renderDayHeader(arg: any) {
    const date = new Date(arg.date);

    let headerText = arg.date.toLocaleDateString(this.calendarOptions.locale, {
      weekday: "long",
      month: "2-digit",
      day: "2-digit",
    });

    if (this.lmpDate && this.ls.isObgyn) {
      const lmp = moment(this.lmpDate).startOf('day');
      const current = moment(date).startOf('day');
      const diffInDays = current.diff(lmp, 'days');
      const weeks = Math.floor(diffInDays / 7);
      const days = diffInDays % 7;
      if (weeks < 43 && weeks >= 0) {
        headerText += ` <span style="color: #ff6347;">(${weeks}w ${days}d)</span>`;
    }
    }

    if (arg.view.type == "dayGridMonth") {
      headerText = arg.date.toLocaleDateString(this.calendarOptions.locale, {
        weekday: "long", // Only show the day name (e.g., "Sunday")
      });
    }

    if (arg.view.type !== "dayGridMonth" && !arg.view.type.startsWith("resource")) {
      headerText += this.transferIconHtml(date);
    }

    return { html: headerText };
  }

  renderDayCell(arg: any) {
    if (arg.view.type !== "dayGridMonth") return undefined;
    return { html: `${arg.dayNumberText}${this.transferIconHtml(arg.date)}` };
  }

  transferIconHtml(date: Date, resourceId?: string): string {
    const dateStr = moment(date).format("YYYY-MM-DD");
    const tooltip = String(
      this.translate.instant("appointments.gui.tooltips.Transfer appointments from this date")
    ).replace(/"/g, "&quot;");
    const resourceAttr = resourceId ? ` data-transfer-resource="${resourceId}"` : "";
    return `<span class="transfer-day-icon material-icons" role="button" data-transfer-date="${dateStr}"${resourceAttr} title="${tooltip}" style="display: none">select_all</span>`;
  }

  updateTransferIcons() {
    setTimeout(() => {
      const container = document.querySelector(".calendar-container");
      if (!container) return;

      this.syncTitleTransferIcon(container);

      container.querySelectorAll("[data-transfer-date]").forEach((node) => {
        const el = node as HTMLElement;
        const dateStr = el.getAttribute("data-transfer-date");
        const resourceId = el.getAttribute("data-transfer-resource");
        const hasAppointments = this.appointmentsFor(dateStr, resourceId).length > 0;
        el.style.display = hasAppointments ? "" : "none";
      });
    });
  }

  syncTitleTransferIcon(container: Element) {
    const title = container.querySelector(".fc-toolbar-title") as HTMLElement | null;
    if (!title) return;

    title.querySelector("[data-transfer-date]")?.remove();

    const showsOneDay = this.viewType === "timeGridDay" || this.viewType === "resourceTimeGridDay";
    if (!showsOneDay) return;

    const calendarApi = this.fullcalendarComponent?.getApi();
    if (!calendarApi) return;

    title.insertAdjacentHTML("beforeend", this.transferIconHtml(calendarApi.getDate()));
  }

  appointmentsFor(dateStr: string, resourceId?: string) {
    return (this.events || []).filter((appointment) => {
      if (moment(appointment.aptTime).format("YYYY-MM-DD") !== dateStr) return false;
      if (!resourceId) return true;

      const eventResourceId = this.appointmentsByRooms ? appointment.RID : appointment.DocUID;
      return String(eventResourceId) === String(resourceId);
    });
  }

  async loadData() {
    this.appointmentsByRooms = this.ls?.premissions?.ApptsByRoom;
    if (this.appointmentsByRooms) {
      this.ls.doctor = null;
      (this.slotDuration = this.ls.premissions?.calendarTimeSlotDuration),
        (this.slotMaxTime = "21:59:00");

      this.calendarOptions = {
        ...this.calendarOptions,
        slotDuration: this.ls.premissions?.calendarTimeSlotDuration,
        slotMaxTime: this.slotMaxTime,
      };
    }
    this.settings.getExams();
    this.showCalendar = true;
    this.settingSub = this.settings.examsSettingsLoaded$.subscribe((exams) => {
      this.getExamsLegend(exams);
    });
    this.getHolidays(new Date().getFullYear());
    this.appointmentsByRooms ? this.refetchRoomsActivityHours() : this.refetchActivityHours();

    await this.settings.getMultiGroups();

    // Set calendar language based on UMT settings:
    this.changeLocale(this.ls.premissions?.interfaceLanguages);
    this.sideCalendarDate = new Date();

    this.setCalendarLineHeight();
  }

  getHolidays(year: number): void {
    if (this.holidayFetchStatus[year]) {
      return;
    }

    this.holidayFetchStatus[year] = "fetching";
    this.settings.getHolidays(year).subscribe({
      next: (holidays) => {
        const newHolidays = holidays.map((holiday) => ({
          title: holiday.hebrew,
          start: holiday.date,
          allDay: true,
          display: "background",
        }));

        this.holidays = [...new Set([...this.holidays, ...newHolidays])];

        this.holidayFetchStatus[year] = "fetched";
        this.initEvents();
      },
      error: (error) => {
        console.error("Error fetching holidays:", error);
        this.holidayFetchStatus[year] = "error";
      },
    });
  }

  private renderEventContent(arg): { domNodes: HTMLElement[] } {
    const eventEl = document.createElement("div");

    // Create and append the custom title element
    const customTitleEl = document.createElement("div");
    customTitleEl.className = "custom-event-title";
    customTitleEl.innerHTML = `${arg.event.title}`;
    eventEl.appendChild(customTitleEl); // Append title first

    // Only create and append the time element if the event is not a background event
    if (arg.event.display !== "background") {
      const customTimeEl = document.createElement("div");
      customTimeEl.className = "custom-event-time";
      eventEl.appendChild(customTimeEl); // Append time after title
    }

    return { domNodes: [eventEl] };
  }

  // On mouse hover tooltip:
  handleEventMouseEnter(event) {
    if (
      !event.event.extendedProps.AutoIndex ||
      event.event.extendedProps.type === "block" ||
      event.event.extendedProps.type === "allDay" ||
      event.event.extendedProps.type === "roomActivityHours"
    )
      return;

    clearTimeout(this.hoverDebounceTimer);
    this.hoverDebounceTimer = setTimeout(() => this._showTooltip(event), 300);
  }

  private _showTooltip(event) {
    this.currentHoverId = event.event.extendedProps?.AutoIndex ?? event.event.id;

    const startDate = new Date(event.event.extendedProps.aptTime);
    const endDate = new Date(startDate.getTime() + event.event.extendedProps.aptlength * 60000); // assuming aptlength is in minutes

    const startTime = startDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const endTime = endDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    let examType = event.event.extendedProps.examName || event.event.extendedProps.Type;
    examType = this.getTranslatedExamType(examType);

    const fullName = `${event.event.extendedProps.First} ${event.event.extendedProps.Last}`;
    const mobile = event.event.extendedProps.Mobile;
    const ID = event.event.extendedProps.ID;
    const appointmentCompleted = event.event.extendedProps.completed;
    const Funding = event.event.extendedProps.Funding;
    const LMP = event.event.extendedProps.LMP;
    const comment = event.event.extendedProps.comment;
    const PID = event.event.extendedProps.PID;

    const isEventConfirmed = event.event.extendedProps.arrivalConfirm;
    const isAppointmentCompleted = event.event.extendedProps.completed;
    const isPatientArrived = event.event.extendedProps.patientArrived;

    const statusIcon = this.getStatusIcon(isAppointmentCompleted, isPatientArrived, isEventConfirmed);

    this.tooltipText = ` ${statusIcon} ${startTime}-${endTime}<br> ${examType}<br> ${fullName}`;

    if (comment) this.tooltipText += `<br> ${comment}`;
    if (ID) this.tooltipText += `<br> ${ID}`;
    if (mobile) this.tooltipText += `<br> ${mobile}`;

    if (Funding === "Private") {
      this.tooltipText += `<br> ${this.settings.hmoMapping["Private"]}`;
    } else if (Funding === "Hmo") {
      const HMO = event.event.extendedProps.HMO;
      const Insurance = event.event.extendedProps.Insurance;
      let tooltipPart = "";
      if (HMO) tooltipPart += `${this.settings.hmoMapping[HMO]}`;
      if (Insurance)
        tooltipPart += (tooltipPart ? " " : "") + `${this.settings.insuranceMapping[Insurance]}`;
      if (tooltipPart) this.tooltipText += `<br> ${tooltipPart}`;
    }

    const examCode = event.event.extendedProps.examCode;
    if (examCode) this.tooltipText += `<br> קוד: ${examCode}`;

    const Fetus = event.event.extendedProps.Fetus;
    if (Fetus == 1) this.tooltipText += `<br> עובר יחיד`;
    else if (Fetus == 2) this.tooltipText += `<br> תאומים`;

    const Price = event.event.extendedProps.Price;
    if (Price) this.tooltipText += `<br>${Price} ש"ח`;

    if (LMP && this.ls.isObgyn) {
      const formattedLMP = new Date(LMP).toLocaleDateString("he-IL");
      this.tooltipText += `<br> LMP: ${formattedLMP}`;
      if (this.lmpGA(LMP, event.event.extendedProps.aptTime)) {
        this.tooltipText += `<br> ${this.lmpGA(LMP, event.event.extendedProps.aptTime)}`;
      }
    }

    if (appointmentCompleted) {
      const translationKey = `appointments.gui.labels.app done`;
      const translated = this.translate.instant(translationKey);
      this.tooltipText += `<br> ${translated}`;
    }

    const currentViewType = event.view.type;
    if (currentViewType === "dayGridMonth") {
      const docName = event.event.extendedProps.Name;
      if (docName) {
        this.tooltipText += `<br> ${docName}`;
      }
    }

    // **Check for other events on the same date with the same PID**
    const eventDate = event.event.extendedProps.aptTime.split("T")[0]; // Extract YYYY-MM-DD
    const currentAutoIndex = event.event.extendedProps.AutoIndex;

    const formatRelEvent = (relEvent: any, showDate = false) => {
      const relStartDate = new Date(relEvent.aptTime);
      const relEndDate = new Date(relStartDate.getTime() + relEvent.aptlength * 60000);
      const relStartTime = relStartDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
      const relEndTime = relEndDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
      const relExamType = this.getTranslatedExamType(relEvent.examName || relEvent.Type || relEvent.examType);
      const datePrefix = showDate
        ? `<b>${relStartDate.toLocaleDateString(this.calendarOptions.locale as string, { day: "2-digit", month: "2-digit", year: "numeric" })}</b><br>`
        : "";
      return `${datePrefix}  ${relEndTime} - ${relStartTime}
        <br>${relExamType}
        ${relEvent.comment ? `<br> ${relEvent.comment}` : ""}
        ${relEvent.Funding === "Private" ? `<br> ${this.settings.hmoMapping["Private"] ?? ""}` : ""}
        ${relEvent.Funding === "Hmo" ? (() => { const parts = [this.settings.hmoMapping[relEvent.HMO], this.settings.insuranceMapping[relEvent.Insurance]].filter(Boolean); return parts.length ? `<br>${parts.join(" ")}` : ""; })() : ""}
        ${relEvent.Price ? `<br> ${relEvent.Price} ש"ח` : ""}
        <hr>`;
    };

    // Same-day other appointments (from already-loaded data)
    const sameDayEvents = (this.eventsByDate[eventDate] || []).filter(
      (e) => e.PID === PID && e.AutoIndex !== currentAutoIndex
    );

    if (sameDayEvents.length > 0) {
      this.tooltipText += `<br><br><b> תורים נוספים להיום:</b><hr>`;
      sameDayEvents.forEach((relEvent) => {
        this.tooltipText += formatRelEvent(relEvent, false);
      });
    }

    this.tooltipHtml = this.safe(this.tooltipText);

    this.tooltipPosition = this.calcTooltipPosition(event.jsEvent.clientX, event.jsEvent.clientY);
    this.eventHover = true;

    // Fetch ALL future appointments for this patient from the API
    const hoveredId = this.currentHoverId;
    const loaderPlaceholder = `<br><br><b>תורים עתידיים:</b><hr><span style="display:inline-flex;align-items:center;gap:8px;opacity:0.7"><span style="width:16px;height:16px;border:2px solid #ccc;border-top-color:#277382;border-radius:50%;display:inline-block;animation:tooltip-spin 0.7s linear infinite"></span> טוען...</span>`;
    this.tooltipText += loaderPlaceholder;
    this.tooltipHtml = this.safe(this.tooltipText);

    this.appointmentService.getFutureAppointmentsByPid(PID).then((futureAppts) => {
      if (!this.eventHover || this.currentHoverId !== hoveredId) return;
      const futureEvents = futureAppts
        .filter((e) => e.AutoIndex !== currentAutoIndex && e.aptTime.split("T")[0] > eventDate)
        .sort((a, b) => new Date(a.aptTime).getTime() - new Date(b.aptTime).getTime());

      let replacement = "";
      if (futureEvents.length > 0) {
        replacement = `<br><br><b>תורים עתידיים:</b><hr>`;
        futureEvents.forEach((relEvent) => {
          replacement += formatRelEvent(relEvent, true);
        });
      }
      this.tooltipText = this.tooltipText.replace(loaderPlaceholder, replacement);
      this.tooltipHtml = this.safe(this.tooltipText);
    });
  }

  handleEventMouseLeave(event: any) {
    clearTimeout(this.hoverDebounceTimer);
    this.eventHover = false;
    this.tooltipText = "";

    // const leavingId = event.event.extendedProps?.AutoIndex ?? event.event.id;

    // // Only hide if we’re leaving the same event that owns the tooltip.
    // if (leavingId === this.currentHoverId) {
    //   clearTimeout(this.tooltipHideTimer);
    //   // Small delay prevents flicker when moving between adjacent events.
    //   this.tooltipHideTimer = setTimeout(() => {
    //     this.eventHover = false;
    //     this.tooltipText = "";
    //     this.currentHoverId = undefined;
    //   }, 80);
    // }
  }

  lmpGA(lmp, time) {
    if (!lmp) return "";
    let dt = new Date(time);
    let days = Math.floor((dt.getTime() - Date.parse(lmp)) / (1000 * 60 * 60 * 24));
    if (Math.floor(days / 7) < 1 || Math.floor(days / 7) > 50) return "";
    return Math.floor(days / 7) + "w " + (Math.floor(days) % 7) + "d";
  }

  getTranslatedExamType(examType: string): string {
    const translationKey = `appointments.gui.exams.${examType}`;
    const translated = this.translate.instant(translationKey);
    return translated === translationKey ? examType : translated;
  }

  async refetchEvents(startDate, endDate, isAll: Event = null) {
    const result = await this.appointmentService.fetchEvents(
      startDate,
      endDate,
      isAll,
      this.isMultiSelect && this.appointmentsByRooms ? this.selectedRooms : undefined,
      this.isMultiSelect && !this.appointmentsByRooms ? this.selectedDoctors : undefined
    );

    this.events = result["events"];
    this.blocks = result["blocks"];
    this.allDayEvents = result["allDayEvents"];
    this.eventsByDate = this.groupEventsByDate(result["events"]);
    this.initEvents();
  }

  groupEventsByDate(events: any[]) {
    return events.reduce((acc, event) => {
      const eventDate = event.aptTime.split("T")[0]; // Extract YYYY-MM-DD
      if (!acc[eventDate]) {
        acc[eventDate] = [];
      }
      acc[eventDate].push(event);
      return acc;
    }, {});
  }

  initEvents() {
    this.calenderFormattedEvents = this.formatEvents(this.events);
    this.calenderFormattedBlocks = this.formatBlocks(this.blocks);
    this.calenderFormattedAllDayEvents = this.formatAllDayEvents(this.allDayEvents);
    this.calenderFormattedResources = this.formatResources(this.resources);

    const combinedItems = [
      ...this.calenderFormattedEvents,
      ...this.calenderFormattedBlocks,
      ...this.calenderFormattedAllDayEvents,
      ...this.calenderFormattedResources,
      ...this.holidays,
    ];

    if (this.appointmentsByRooms && this.ls.room) {
      combinedItems.push(...this.roomHours);
    }

    this.calendarOptions = { ...this.calendarOptions, events: combinedItems };
  }

  handleViewChange(viewType: string = this.viewType) {
      setTimeout(() => {
        if (this.isMultiSelect) {
          if (this.appointmentsByRooms) {
            this.showPrintIcon = viewType === "resourceTimeGridDay" && this.selectedRooms?.length > 0;
          } else {
            this.showPrintIcon = viewType === "resourceTimeGridDay" && this.selectedDoctors?.length > 0;
          }
        } else {
          this.showPrintIcon =
            (viewType === "dayGridDay" || viewType === "timeGridDay" || viewType === "listDay") &&
            this.ls.doctor;
  
          if (this.appointmentsByRooms) {
            this.showPrintIcon =
              (viewType === "dayGridDay" || viewType === "timeGridDay" || viewType === "listDay") &&
              this.ls.room;
          }
        }
      });

    this.showErrorModal = false;
    this.showExamErrorModal = false;
  }

  refetchActivityHours() {
    if (!this.ls.doctor) {
      this.businessHours = false;
      this.calendarOptions = { ...this.calendarOptions, businessHours: this.businessHours };
      return;
    }
    this.settings.getActivityHours(true).subscribe({
      next: (data) => {
        this.businessHours = data;
        this.calendarOptions = { ...this.calendarOptions, businessHours: this.businessHours };
      },
      error: (error) => {
        console.error("Error refetchActivityHours:", error);
        this.businessHours = false;
        this.calendarOptions = { ...this.calendarOptions, businessHours: this.businessHours };
      },
    });
  }

  refetchRoomsActivityHours() {
    if (!this.ls.room || this.ls.room == "No Room") {
      this.roomHours = [];
      return;
    }
    this.settings.getActivityHours(true, true).subscribe({
      next: (data) => {
        const roomHours = data;
        const roomHoursFormatted = this.extendRoomHoursToDates(roomHours);
        this.roomHours = roomHoursFormatted;
        this.initEvents();
      },
      error: (error) => {
        console.error("Error refetchRoomsActivityHours:", error);
        this.roomHours = [];
      },
    });
  }

  extendRoomHoursToDates(roomHours: any[]): any[] {
    const startDate = new Date(this.startDateDisplayWithGap);
    const endDate = new Date(this.endDateDisplayWithGap);
    const roomHoursWithDates = [];

    for (const slot of roomHours) {
      let currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        // Check if the current date corresponds to the slot's day of the week
        const dayInSlot = currentDate.getDay() === slot.daysOfWeek[0];
        const from = slot.oneTimeDate ? new Date(new Date(slot.oneTimeDate).toDateString()) : null;
        const to = new Date(new Date(slot.oneTimeDateTo || slot.oneTimeDate).toDateString());
        const inOneTimeRange = !slot.isOneTime || !slot.oneTimeDate ||
          (currentDate >= from && currentDate <= to);
        if (dayInSlot && inOneTimeRange) {
          const slotStart = new Date(currentDate);
          const slotEnd = new Date(currentDate);

          const [startHour, startMinute] = slot.startTime.split(":").map(Number);
          const [endHour, endMinute] = slot.endTime.split(":").map(Number);

          slotStart.setHours(startHour, startMinute);
          slotEnd.setHours(endHour, endMinute);
          let examType = slot.examType;
          if (slot.temp) {
            const tempStart = new Date(slot.temp.startTime);
            const tempEnd = new Date(slot.temp.endTime);

            // Check if the current date is within the temp range and if it's the same day of the week
            if (
              currentDate >= tempStart &&
              currentDate <= tempEnd &&
              slot.daysOfWeek.includes(currentDate.getDay())
            ) {
              examType = slot.temp.examType;
            }
          }
          roomHoursWithDates.push({
            //  ...slot,
            days: [...slot["daysOfWeek"]],
            type: "roomActivityHours",
            start: slotStart.toISOString(),
            end: slotEnd.toISOString(),
            allDay: false,
            display: "background",
            backgroundColor: "initial",
            background: this.getEventTypeColor(examType),
            TimeStart: slot.startTime,
            TimeEnd: slot.endTime,
          });
        }

        // Move to the next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    return roomHoursWithDates;
  }

  private calcTooltipPosition(clientX: number, clientY: number) {
    const TOOLTIP_W = 380;
    const TOOLTIP_MAX_H = window.innerHeight * 0.8;
    const GAP = 10;
    const spaceBelow = window.innerHeight - clientY - GAP;
    const spaceAbove = clientY - GAP;
    const flipX = clientX + TOOLTIP_W > window.innerWidth;
    const flipY = spaceBelow < TOOLTIP_MAX_H && spaceAbove > spaceBelow;
    return {
      top:       flipY ? "auto"                              : `${clientY + GAP}px`,
      bottom:    flipY ? `${window.innerHeight - clientY + GAP}px` : "auto",
      left:      flipX ? "auto"                              : `${clientX + GAP}px`,
      right:     flipX ? `${window.innerWidth - clientX + GAP}px`  : "auto",
      maxHeight: flipY ? `${spaceAbove}px`                   : `${spaceBelow}px`,
    };
  }

  private materialIcon(name: string, color?: string): string {
    return `<span
    class="material-symbols-outlined"
    style="
      font-size: 1.5em;
      vertical-align: middle;
      line-height: 1;
      ${color ? `color: ${color};` : ''}
    "
  >${name}</span>`;
  }

  private getStatusIcon(completed: any, patientArrived: any, arrivalConfirm: any): string {
    // Icon 1: confirmation status
    let icon1 = "";
    if (arrivalConfirm === true)  icon1 = this.materialIcon("check_circle");
    else if (arrivalConfirm === false) icon1 = this.materialIcon("unpublished", "#e53935");

    // Icon 2: physical arrival / completion
    let icon2 = "";
    if (completed === true)            icon2 = this.materialIcon("person_check");
    else if (patientArrived === true)  icon2 = this.materialIcon("person");
    else if (patientArrived === false) icon2 = this.materialIcon("person_cancel");

    return `${icon1}${icon2}`;
  }

  formatEvents(events: any[]) {
    return events.map((event) => {
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };

      const startDate = new Date(event.aptTime);
      const endDate = this.calculateEndTime(new Date(event.aptTime), event.aptlength);

      const startString = startDate.toLocaleTimeString([], timeOptions);
      const endString = endDate.toLocaleTimeString([], timeOptions);

      const isEventConfirmed = event.arrivalConfirm;
      const isAppointmentCompleted = event.completed;
      const isPatientArrived = event.patientArrived;

      const comment = event.comment;

      const statusIcon = this.getStatusIcon(isAppointmentCompleted, isPatientArrived, isEventConfirmed);

      return {
        ...event,
        title: `${statusIcon} ${event.First} ${event.Last}${
          comment ? ` (${comment})` : ""
        } \u200E${startString} - ${endString}`,
        start: new Date(event.aptTime),
        end: this.calculateEndTime(new Date(event.aptTime), event.aptlength),
        color: event.customColor ? event.customColor : this.getEventTypeColor(event.examTypeID),
        resourceId: this.appointmentsByRooms ? Number(event.RID) : Number(event.DocUID),
      };
    });
  }

  formatResources(events: any[]) {
    return events.map((event) => {
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };

      const startDate = new Date(event.aptTime);
      const endDate = this.calculateEndTime(new Date(event.aptTime), event.aptlength);

      const startString = startDate.toLocaleTimeString([], timeOptions);
      const endString = endDate.toLocaleTimeString([], timeOptions);

      const isEventConfirmed = event.arrivalConfirm;
      const isAppointmentCompleted = event.completed;
      const isPatientArrived = event.patientArrived;

      const comment = event.comment;

      const statusIcon = this.getStatusIcon(isAppointmentCompleted, isPatientArrived, isEventConfirmed);

      return {
        ...event,
        title: `${statusIcon} ${event.First} ${event.Last}${
          comment ? ` (${comment})` : ""
        } \u200E${startString} - ${endString}`,
        start: new Date(event.aptTime),
        end: this.calculateEndTime(new Date(event.aptTime), event.aptlength),
        color: event.customColor ? event.customColor : this.getEventTypeColor(event.examTypeID),
        resourceId: this.appointmentsByRooms ? event.RID?.toString() : event.DocUID?.toString(),
      };
    });
  }

  formatBlocks(blocks) {
    return blocks.map((block) => {
      const isRecurring = block.isRecurring === true;
      const start = new Date(block.startTime);
      const end = new Date(block.endTime);
      const startTime = start.toTimeString().slice(0, 5);
      const endTime = end.toTimeString().slice(0, 5);

      const eventObj: any = {
        ...block,
        type: "block",
        aptTime: block.startTime,
        blockEndTime: block.endTime,
        aptlength: block.duration,
        title: block.comment || "",
        color: "#a4cd9d",
        resourceId: this.appointmentsByRooms ? Number(block.RID) : Number(block.DocUID),
      };

      if (isRecurring && block.daysOfWeek && block.repeatUntil && block.startTime) {
        eventObj.daysOfWeek = block.daysOfWeek.split(",").map((day) => parseInt(day, 10));
        eventObj.startTime = startTime;
        eventObj.endTime = endTime;
        eventObj.duration = `PT${block.duration}M`;

        eventObj.startRecur = new Date(block.startTime).toISOString();
        eventObj.endRecur = momentTZ.tz(block.repeatUntil, "Asia/Jerusalem").endOf("day").toISOString();
      } else {
        eventObj.start = new Date(block.startTime);
        eventObj.end = this.calculateEndTime(new Date(block.startTime), block.duration);
      }

      eventObj.extendedProps = {
        ...eventObj,
        daysOfWeek: block.daysOfWeek,
        repeatUntil: block.repeatUntil,
        isRecurring: block.isRecurring,
      };

      return eventObj;
    });
  }

  formatAllDayEvents(allDayEvents) {
    return allDayEvents.map((event) => ({
      ...event,
      type: "allDay",
      allDay: true,
      aptTime: event.startTime,
      eventEndTime: event.endTime,
      aptlength: event.duration,
      start: new Date(event.startTime),
      end: this.calculateEndTime(new Date(event.startTime), event.duration),
      color: "#9659f7",
      title: event.comment ? event.comment : "",
      resourceId: this.appointmentsByRooms ? Number(event.RID) : Number(event.DocUID),
    }));
  }

  calculateEndTime(startTime: Date, duration: number): Date {
    const startMoment = moment(startTime);
    const endMoment = startMoment.add(duration, "minutes");
    return endMoment.toDate();
  }

  ngOnDestroy(): void {
    if (this.settingSub) {
      this.settingSub.unsubscribe();
    }
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    this.appointmentService.closeExamCompletedSocket();
    document.removeEventListener("click", this.handleTransferIconClick, true);

    clearTimeout(this.tooltipHideTimer);
  }

  getEventTypeColor(typeID: string) {
    const typeColorMapper = this.settings.examsSettings;
    const exam = typeColorMapper.find((exam) => exam.AutoIndex == typeID);
    return exam ? exam.Color : null; /**default value is blue of calendar */
  }

  handleCalendarToggle() {
    this.calendarVisible = !this.calendarVisible;
  }

  handleWeekendsToggle() {
    const { calendarOptions } = this;
    calendarOptions.weekends = !calendarOptions.weekends;
  }

  getpxPerMinute(element) {
    const pxPerSlot = element.offsetHeight ?? 0;
    // turn that into px‑per‑minute
    const slotMinutes = 30;
    return pxPerSlot / slotMinutes;
  }

  // Selecting a date range:
  handleDateSelect(selectInfo: DateSelectArg) {
    //calculate offset to 5 minutes for by dividing the offset by pixels per half hour slot and then round to the nearest 5 minutes
    if (this.yTime) {
      //set the time component selectInfo.start to yTime
      const date = new Date(selectInfo.start);
      const hours = this.yTime.split(":")[0];
      const minutes = this.yTime.split(":")[1];
      date.setHours(Number(hours), Number(minutes), 0, 0);
      //find the offset beween the start date and the selected date and adjust the end date accordingly
      const offset = selectInfo.start.getTime() - date.getTime();
      const newEnd = new Date(selectInfo.end.getTime() + offset);
      selectInfo.end = newEnd;
      selectInfo.start = date;
    } else {
      let offset = selectInfo.jsEvent.offsetY / this.getpxPerMinute(selectInfo.jsEvent.srcElement);
      offset = Math.round(offset / 5) * 5;
      selectInfo.start = new Date(selectInfo.start.getTime() + offset * 60000);
      const newEnd = new Date(selectInfo.end.getTime() + offset * 60000);
      selectInfo.end = newEnd;
    }
    if (this.isMultiSelect) {
      if (selectInfo.allDay) {
        const resourceId = selectInfo.resource._resource.id;
        this.resourceId = resourceId;

        if (this.selectedExamId) {
          const exam = this.getExamById(this.selectedExamId);

          if (!exam) {
            return;
          }

          const name = this.getExamDisplayName(exam);
          this.selectedExamLength = exam?.Length ?? 0; // keep the length

          const ok = this.canPerformInCurrentContext(exam, resourceId);

          if (!ok) {
            this.examErrorName = name;
            this.showExamErrorModal = true;
            this.showSelection = false;
            return;
          }
        }

        this.allDayEventService.startDate = selectInfo.start;
        const updatedEnd = new Date(selectInfo.end.getTime());
        updatedEnd.setDate(updatedEnd.getDate() - 1);
        updatedEnd.setHours(23, 59, 59, 999);
        this.allDayEventService.endDate = updatedEnd;

        this.allDayEventService.duration = this.allDayEventService.calculateDuration(
          selectInfo.start,
          selectInfo.end
        );
        this.allDayEventService.AutoIndex = null;

        this.showAllDayModal = true;
        this.showSelection = false;
      } else {
        const resourceId = Number(selectInfo.resource._resource.id);
        this.resourceId = resourceId;
        if (this.selectedExamId) {
          const exam = this.getExamById(this.selectedExamId);

          if (!exam) {
            return;
          }

          const name = this.getExamDisplayName(exam);
          this.selectedExamLength = exam?.Length ?? 0; // keep the length

          const ok = this.canPerformInCurrentContext(exam, resourceId);

          if (!ok) {
            this.examErrorName = name;
            this.showExamErrorModal = true;
            this.showSelection = false;
            return;
          }
        }

        this.selectedDateInfo = selectInfo;
        this.showSelection = true;
        this.selectedEventInfo = null;
      }
    } else {
      if (!this.ls.room && this.appointmentsByRooms) {
        this.showErrorModal = true;
        return;
      }
      if (!this.ls.doctor && !this.appointmentsByRooms) {
        this.showErrorModal = true;
        return;
      }
      if (this.selectedExamId) {
        const exam = this.getExamById(this.selectedExamId);

        if (!exam) {
          return;
        }

        const name = this.getExamDisplayName(exam);
        this.selectedExamLength = exam?.Length ?? 0; // keep the length

        const ok = this.canPerformInCurrentContext(exam);

        if (!ok) {
          this.examErrorName = name;
          this.showExamErrorModal = true;
          this.showSelection = false;
          return;
        }
      }

      this.selectedDateInfo = selectInfo;

      if (selectInfo.allDay) {
        this.showSelection = false;
        this.allDayEventService.startDate = selectInfo.start;

        const updatedEnd = new Date(selectInfo.end.getTime());
        updatedEnd.setDate(updatedEnd.getDate() - 1);
        updatedEnd.setHours(23, 59, 59, 999);
        this.allDayEventService.endDate = updatedEnd;

        this.allDayEventService.duration = this.allDayEventService.calculateDuration(
          selectInfo.start,
          selectInfo.end
        );
        this.allDayEventService.AutoIndex = null;
        this.showAllDayModal = true;
        this.showSelection = false;
      } else {
        if (this.switchDocMode) {
          this.selectedEventInfo = {
            ...this.selectedEventInfo,
            DocUID: this.ls.doctor,
            RID: this.ls.room,
          };
          this.openAppointmentWithNewDate();
        } else {
          this.showSelection = true;
          this.selectedEventInfo = null;
        }
      }
    }
    return;
  }

  private getExamById(examId: number | string) {
    return this.examsCache.find((e) => String(e.AutoIndex) === String(examId));
  }

  private getExamDisplayName(exam: any): string {
    return exam?.examName || exam?.examType || "(unnamed exam)";
  }

  private canPerformInCurrentContext(exam: any, recourceID = null): boolean {
    if (!exam || !Array.isArray(exam.users)) return false;

    let idToCheck: number | null = null;
    if (this.isMultiSelect) {
      if (this.appointmentsByRooms) {
        idToCheck = Number(recourceID);
      } else {
        idToCheck = Number(recourceID);
      }
    } else {
      if (this.appointmentsByRooms) {
        idToCheck = Number(this.ls?.room);
      } else {
        idToCheck = Number(this.ls?.doctor);
      }
    }

    if (!Number.isFinite(idToCheck)) return false;

    return exam.users.includes(idToCheck);
  }

  handelAddAppointment(selectInfo = this.selectedDateInfo) {
    this.showAlert = false;
    let workHours: boolean | any[] = false;
    if (this.appointmentsByRooms) {
      workHours = this.roomHours;
    } else {
      workHours = this.businessHours;
    }
    const inBusinessHours = this.checkBusinessHours(
      selectInfo.start,
      selectInfo.end,
      workHours,
      this.appointmentsByRooms ? true : false
    );
    if (!inBusinessHours) {
      this.showAlert = true;
      return;
    }
    this.addAppointment();
  }

  handleCloseSearchModal(event) {
    this.showSearch = false;
    if (!event) return;
    this.ls.room = event.RID;
    this.goToDate(event.start);
    this.refetchRoomsActivityHours();
    this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
    const start = new Date(event.start);
    const end = new Date(start.getTime() + event.duration * 60000);
    this.selectedDateInfo = {
      start: start,
      end: end,
      examType: event.examType,
      duration: event.duration,
    };
    this.addAppointment();
  }

  addAppointment(selectInfo = this.selectedDateInfo) {
    this.showSelection = false;
    this.appointmentService.startDate = selectInfo.start;
    this.appointmentService.endDate = selectInfo.end;
    this.appointmentService.duration = selectInfo.duration;
    this.appointmentService.AutoIndex = null;
    this.appointmentService.examType = selectInfo.examType;
    this.appointmentService.lmp = this.lmpDate;
    this.lmpDate = null;

    if (this.selectedExamId) {
      this.appointmentService.examType = this.selectedExamId;
      this.appointmentService.examLength = this.selectedExamLength;
    }

    if (this.patientsService.currentPatient) {
      this.showAppointment = true;
    } else {
      this.showSelectPatient = true;
    }

    const calendarApi = selectInfo?.view?.calendar;
    if (calendarApi) calendarApi.unselect(); // clear date selection
  }

  checkBusinessHours(startDate, endDate, businessHours, rooms = false) {
    if (!businessHours || !businessHours.length) return true;
    const start = startDate.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" });
    const end = endDate.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" });

    const startDay = new Date(start).getDay();
    const endDay = new Date(end).getDay();
    const startTime = new Date(start).toTimeString().substr(0, 5);
    const endTime = new Date(end).toTimeString().substr(0, 5);

    const startDayHours = rooms
      ? businessHours.filter((d) => d.days.includes(startDay))
      : businessHours.filter((d) => d.daysOfWeek.includes(startDay));
    const endDayHours = rooms
      ? businessHours.filter((d) => d.days.includes(endDay))
      : businessHours.filter((d) => d.daysOfWeek.includes(endDay));

    const isValidStart = rooms
      ? startDayHours.some((d) => startTime >= d.TimeStart && startTime < d.TimeEnd)
      : startDayHours.some((d) => startTime >= d.startTime && startTime < d.endTime);

    const isValidEnd = rooms
      ? endDayHours.some((d) => endTime > d.TimeStart && endTime <= d.TimeEnd)
      : endDayHours.some((d) => endTime > d.startTime && endTime <= d.endTime);

    return isValidStart && isValidEnd;
  }

  // Clicking on an event:
  handleEventClick(clickInfo) {
    this.eventHover = false;
    this.selectedEventInfo = clickInfo.event.extendedProps;
    this.lmpDate = null;

    if (this.selectedEventInfo.type === "block") {
      this.BlockService.setBlock(this.selectedEventInfo);
      this.showBlock = true;
    } else if (this.selectedEventInfo.type === "allDay") {
      this.allDayEventService.setEvent(this.selectedEventInfo);
      this.showAllDayModal = true;
    }
    // appointment
    else {
      this.appointmentService.setAppointment(this.selectedEventInfo);
      this.patientsService.setPat(
        new Patient(this.selectedEventInfo.ID, this.selectedEventInfo.First, this.selectedEventInfo.Last)
      );
      this.patientsService.currentPatient.PID = this.selectedEventInfo.PID;
      this.patientsService.currentPatient.Mobile = this.selectedEventInfo.Mobile;
      this.patientsService.currentPatient.HMO = this.selectedEventInfo.HMO;
      this.patientsService.currentPatient.Insurance = this.selectedEventInfo.Insurance;
      this.showAppointment = true;
      // load the lmp from selected patient:
      if (!this.lmpDate && this.ls.isObgyn) {
        this.lmpDate = this.selectedEventInfo.LMP ? moment(this.selectedEventInfo.LMP) : null;
        // this.calculatePregnancyAge();
      }
    }
  }

  handleEvents(events: EventApi[]) {
    this.currentEvents = events;
    this.updateTransferIcons();
  }

  openAppointmentWithNewDate(selectInfo = this.selectedDateInfo) {
    this.showSelection = false;
    this.appointmentService.startDate = selectInfo.start;
    this.appointmentService.endDate = selectInfo.end;
    this.appointmentService.duration = this.selectedEventInfo.aptlength;
    // this.appointmentService.calculateDuration(
    //   selectInfo.start,
    //   selectInfo.end
    // );
    this.appointmentService.AutoIndex = this.selectedEventInfo.AutoIndex;

    if (this.patientsService.currentPatient) {
      this.showAppointment = true;
    } else {
      this.showSelectPatient = true;
    }

    const calendarApi = selectInfo.view.calendar;

    calendarApi.unselect(); // clear date selection
  }

  handleCloseAppointmentModal(saveAppointment) {
    this.showAppointment = false;
    this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
    this.appointmentsByRooms ? this.refetchRoomsActivityHours() : this.refetchActivityHours();

    // reset exam selection
    this.selectedExamId = "";
    this.selectedExamLength = 0;
    this.selectedExamName = "";

    if (typeof saveAppointment === "object" && saveAppointment !== null) {
      this.switchDocMode = true;
    } else {
      this.selectedEventInfo = null;
      this.patientsService.resetPat();
      this.switchDocMode = false;
       this.lmpDate = null;
    }
  }

  handleClosePatientSearchModal(openOtherModalName) {
    this.showSelectPatient = false;
    if (openOtherModalName == "patDetails") {
      this.showPatDetails = true;

      // load the lmp from selected patient:
      if (!this.lmpDate && this.ls.isObgyn) {
        this.lmpDate = this.patientsService.currentPatient?.LMP
          ? moment(this.patientsService.currentPatient.LMP)
          : null;
        // this.calculatePregnancyAge();
      }
    }
    if (openOtherModalName == "appointmentDetails") {
    }
  }

  handleClosePatDetailsModal(openOtherModalName) {
    this.showPatDetails = false;

    if (!openOtherModalName) {
      this.lmpDate = null;
      this.patientsService.resetPat();
      return;
    }

    // Refresh lmpDate from the (possibly just-saved) currentPatient
    if (this.ls.isObgyn) {
      this.lmpDate = this.patientsService.currentPatient?.LMP
        ? moment(this.patientsService.currentPatient.LMP)
        : null;
      this.appointmentService.lmp = this.lmpDate;
    }

    if (this.page == "Calendar") this.showAppointment = true;
    if (this.page == "Patients") this.showPat = true;
  }

  handleCloseBlockModal() {
    this.showBlock = false;

    if (this.isMultiSelect) {
      if (this.appointmentsByRooms) {
        this.onRoomsChange({ value: this.selectedRooms });
      } else {
        this.onDoctorsChange({ value: this.selectedDoctors });
      }
      this.selectedEventInfo = null;
    } else {
      this.selectedEventInfo = null;
      this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
    }
  }

  handleCloseAllDayEventModal() {
    this.showAllDayModal = false;

    if (this.isMultiSelect) {
      if (this.appointmentsByRooms) {
        this.onRoomsChange({ value: this.selectedRooms });
      } else {
        this.onDoctorsChange({ value: this.selectedDoctors });
      }

      this.selectedEventInfo = null;
    } else {
      this.selectedEventInfo = null;
      this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
    }
  }

  getDoctors() {
    return this.ls.clinicUsers;
  }

  handelBlock(selectInfo = this.selectedDateInfo) {
    const resourceId = selectInfo?.resource?._resource.id;
    this.resourceId = resourceId;
    this.showSelection = false;
    this.BlockService.startDate = selectInfo.start;
    this.BlockService.endDate = selectInfo.end;
    this.BlockService.duration = this.BlockService.calculateDuration(selectInfo.start, selectInfo.end);
    this.BlockService.AutoIndex = null;
    this.showBlock = true;
  }

  // Drag appointment:
  async handleEventDrop(info) {
    this.appointmentAutoIndex = null;
    this.patientPID = null;
    // Extract new start and end dates from the event after drop
    const { event } = info;
    const newStart = event.start;
    const newEnd = event.end;
    let result;
    let isUpdateAppt;
    try {
      this.selectedEventInfo = { ...event.extendedProps };
      if (event.extendedProps.type == "block") {
        isUpdateAppt = false;
        this.BlockService.setBlock(this.selectedEventInfo);
        const newDuration = this.BlockService.calculateDuration(newStart, newEnd);
        const updatedBlock = this.selectedEventInfo;
        updatedBlock["duration"] = newDuration;
        updatedBlock["startHour"] = this.appointmentService.appointmentHour(new Date(newStart));
        updatedBlock["endHour"] = this.appointmentService.appointmentHour(new Date(newEnd));
        updatedBlock["blockStartDate"] = new Date(newStart);
        updatedBlock["blockEndDate"] = new Date(newEnd);
        result = await this.BlockService.updateBlock(updatedBlock);
      } else if (event.extendedProps.type === "allDay") {
        isUpdateAppt = false;
        this.allDayEventService.setEvent(this.selectedEventInfo);
        const newDuration = this.allDayEventService.calculateDuration(newStart, newEnd);
        const updatedEvent = this.selectedEventInfo;
        updatedEvent["duration"] = newDuration;
        updatedEvent["startHour"] = this.appointmentService.appointmentHour(new Date(newStart));
        updatedEvent["endHour"] = this.appointmentService.appointmentHour(new Date(newEnd));
        updatedEvent["eventStartDate"] = new Date(newStart);
        updatedEvent["eventEndDate"] = new Date(newEnd);
        result = await this.allDayEventService.updateEvent(updatedEvent);
      }
      // appointment
      else {
        this.appointmentService.setAppointment(this.selectedEventInfo);
        const newDuration = this.appointmentService.calculateDuration(newStart, newEnd);
        const updatedAppointment = this.selectedEventInfo;
        updatedAppointment["duration"] = newDuration;
        updatedAppointment["startHour"] = this.appointmentService.appointmentHour(new Date(newStart));
        updatedAppointment["appointmentDate"] = new Date(newStart);

        result = await this.appointmentService.updateAppointment(updatedAppointment);
        isUpdateAppt = true;
      }
      const noChanges = result["noChanges"];
      const ok = result["status"] === "ok";
      const ManualSMS = this.ls?.premissions?.ManualSMS;
      if (ok) {
        if (!noChanges && ManualSMS && isUpdateAppt) {
          this.askSendSMS = true;
          this.appointmentAutoIndex = this.selectedEventInfo.AutoIndex;
          this.patientPID = this.selectedEventInfo.PID;
        } else {
          this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
        }
      }
    } catch (error) {
      info.revert();
      console.error("Error during the update Appointment:", error);
    }
  }

  setPage(page: string, resetPatient: boolean, examId: string = "",openLastAppointment: boolean = false,restLastAppointment=false) {
    this.page = page;
    this.showPage$.next(this.page);
    this.ls.tabChanged$.next(this.page);
    this.showPat = false;

    // clear selected exam
    if (page !== "Calendar") {
      this.selectedExamId = "";
      this.selectedExamLength = 0;
      this.selectedExamName = "";
    }

    if (page === "Patients") {
      this.showSelectPatient = true;
    }
    if(restLastAppointment){
      this.appointmentService.resetService();
      this.selectedEventInfo = null;
      this.switchDocMode = false;

    }
    if (resetPatient) {
      this.patientsService.currentPatient = null;
    }

    if (page === "Calendar") {
      this.showSelectPatient = false;
      this.lmpDate = this.patientsService.currentPatient?.LMP
        ? moment(this.patientsService.currentPatient.LMP)
        : null;
      if (this.lmpDate && this.ls.isObgyn) {
        // this.calculatePregnancyAge();
      }

      if (this.isMultiSelect) {
        setTimeout(() => {
          const calendarApi = this.fullcalendarComponent?.getApi?.();
          if (!calendarApi) return;

          if (this.ls.premissions.ApptsByRoom) {
            this.onRoomsChange({ value: this.selectedRooms });
          } else {
            this.onDoctorsChange({ value: this.selectedDoctors });
          }

          calendarApi.setOption("resources", this.calenderFormattedResources);
          calendarApi.changeView("resourceTimeGridDay");
        }, 0);
      }
    }

    if (page === "List") {
      this.lmpDate = null;
      this.showSelectPatient = false;
    }

    if (page === "Leads") {
      this.lmpDate = null;
      this.showSelectPatient = false;
    }

    if (page === "Appointments Stats" || page === "Financial Report") {
      this.lmpDate = null;
      this.showSelectPatient = false;
    }
    
    if (examId) {
      this.selectedExamId = examId;
      this.getExamNameById(examId);
    }
    if (openLastAppointment) {
     this.navToLastAppts()
    }
  }

  onPatientPageChange(e: {
    page: string;
    resetPatient: boolean;
    saveAppointment?: any;
    examId?: string;
    navToLastAppt?: boolean;
  }) {
    this.setPage(e.page, e.resetPatient, e.examId, e.navToLastAppt);

    if (e.page === "Calendar" && e.saveAppointment) {
      // initialize services & open modal
      const appt = e.saveAppointment;
      this.selectedEventInfo = appt;

      if (appt.type === "block") {
        this.BlockService.setBlock(appt);
        this.showBlock = true;
      } else if (appt.type === "allDay") {
        this.allDayEventService.setEvent(appt);
        this.showAllDayModal = true;
      } else {
        this.appointmentService.setAppointment(appt);
        // set current patient
        this.patientsService.setPat(new Patient(appt.ID, appt.First, appt.Last));
        // copy over the rest of the fields
        Object.assign(this.patientsService.currentPatient, {
          PID: appt.PID,
          Mobile: appt.Mobile,
          HMO: appt.HMO,
          Insurance: appt.Insurance,
        });
        this.showAppointment = true;
      }

      // run close/refetch logic
      this.handleCloseAppointmentModal(appt);

      // Restore LMP for calendar display (handleCloseAppointmentModal nulls it)
      // This block only runs when navigating to the calendar to reschedule (e.saveAppointment is set)
      if (this.ls.isObgyn && appt.type !== "block" && appt.type !== "allDay") {
        this.lmpDate = appt.LMP ? moment(appt.LMP) : null;
      }
    }
  }

  getExamNameById(examId: number | string): string | null {
    if (!examId) return null;

    const exam = this.examsCache.find((e) => String(e.AutoIndex) === String(examId));

    if (exam) {
      const name = exam.examName || exam.examType || "(no name)";
      this.selectedExamName = name;
      return name;
    } else {
      return null;
    }
  }

  goToDate(date: Date) {
    this.ignoreFirstDatesSetTrigger = true;
    date = new Date(date);
    const calendarApi = this.fullcalendarComponent.getApi();
    calendarApi.changeView("timeGridDay");
    calendarApi.gotoDate(date);
  }

  async getExamsLegend(exams) {
    let legend = {};
    for (let i = 0; i < exams.length; i++) {
      let exam = exams[i];
      if (exam["custom"]) {
        legend[exam.examType] = { color: exam["Color"], isCustom: exam["custom"] };
      }
      if (exam["examName"]) {
        legend[exam.examName] = { color: exam["Color"], isCustom: exam["custom"] };
      } else {
        legend[exam.examType] = { color: exam["Color"], isCustom: exam["custom"] };
      }
    }
    this.examsLegend = legend;
  }

  goToSettings() {
    this.router.navigate(["/settings"]);
  }

  openAttendanceTracker() {
    this.router.navigate(["/attendance"]);
  }

  handleDatesSet(params) {
    this.viewType = params.view.type;
    this.handleViewChange(this.viewType);
    
    if (this.ignoreFirstDatesSetTrigger) {
      this.ignoreFirstDatesSetTrigger = false;
      return;
    }
    
    // Navigate the MatCalendar to the month displayed in FullCalendar:
    if (this.sideCalendar) {
      this.sideCalendarDate = moment(params.start);
      this.sideCalendar._goToDateInView(this.sideCalendarDate, "month");
    }

    const startYear = params.start.getFullYear();
    const endYear = params.end.getFullYear();

    if (startYear === endYear) {
      this.getHolidays(startYear);
    } else {
      this.getHolidays(startYear);
      this.getHolidays(endYear);
    }

    // add 1 day gap to handle appointments or block that continue from the days before to this days
    const startDayWithGap = moment(params.start).subtract(1, "d").toISOString();
    const endDayWithGap = moment(params.end).add(1, "d").toISOString();
    this.startDateDisplay = params.start;
    this.endDateDisplay = params.end;
    this.startDateDisplayWithGap = startDayWithGap;
    this.endDateDisplayWithGap = endDayWithGap;
    this.refetchEvents(startDayWithGap, endDayWithGap);
    this.appointmentsByRooms ? this.refetchRoomsActivityHours() : null;
  }

  print() {
    const startDate = new Date(this.startDateDisplay).toISOString();

    if (this.isMultiSelect) {
      if (this.appointmentsByRooms) {
        const roomIDs = this.selectedRooms.join(",");
        const printUrl = `api/print/multiRoom/${startDate}/null?roomIDs=${roomIDs}`;
        window.open(printUrl);
      } else {
        const doctorIDs = this.selectedDoctors.join(",");
        const printUrl = `api/print/multiDoctor/${startDate}/null?docUIDs=${doctorIDs}`;
        window.open(printUrl);
      }
    } else {
      if (this.appointmentsByRooms) {
        const printUrl = `api/print/roomDay/${startDate}/null?roomID=${this.ls.room}`;
        window.open(printUrl);
      } else {
        const printUrl = `api/print/day/${startDate}/${this.ls.doctor}`;
        window.open(printUrl);
      }
    }
  }

  redirectAdministration() {
    const type = this.ls.isCardio
      ? "cardio"
      : this.ls.isGastro
      ? "gastro"
      : this.ls.isEyes
      ? "eyes"
      : this.ls.isUSG
      ? "usg"
      : "aweb";
    this.http.post("/api/administration", { type }).subscribe((data: string) => {
      window.location.href = data;
    });
  }
  redirectAdministrationPatient() {
    const type = this.ls.isCardio
      ? "cardio"
      : this.ls.isGastro
      ? "gastro"
      : this.ls.isEyes
      ? "eyes"
      : this.ls.isUSG
      ? "usg"
      : "aweb";
    this.http.post("/api/administration", { type }).subscribe((data: string) => {
      window.location.href = data + "/" + this.patientsService.currentPatient.PID;
    });
  }

  getButtonText(): string {
    let domainUrl = "";
    if (this.ls.isCardio) {
      domainUrl = "Cardio";
    }
    if (this.ls.isGastro) {
      domainUrl = "Gastro";
    }

    if (this.ls.isEyes) {
      domainUrl = "Eyes";
    }
    if (this.ls.isUSG) {
      domainUrl = "USG";
    }
    if (this.ls.isObgyn) {
      domainUrl = "OBGYN";
    }
    return domainUrl;
  }

  goBillingsPatient(patient: any) {
    this.patientsService.getPatient(patient.PID, () => {
      this.router.navigate(["/billing"]);
    });
  }

  changeLocale(language: string) {
    let todayButtonText = this.translate.instant("appointments.gui.calendar.today");
    let toggleResourceView = this.isMultiSelect
      ? this.translate.instant("appointments.gui.calendar.defaultView")
      : this.translate.instant(
          this.ls?.premissions?.ApptsByRoom
            ? "appointments.gui.calendar.multiSelect.room"
            : "appointments.gui.calendar.multiSelect.doctor"
        );

    switch (language) {
      case "English":
      case "en-US":
        language = "en-gb";
        break;
      case "Hebrew":
      case "he-IL":
        language = "he";
        break;
      case "Italian":
      case "it-IT":
        language = "it";
        break;
      case "Polish":
      case "pl-PL":
        language = "pl";
        break;
      case "Spanish":
      case "es-ES":
        language = "es";
        break;
      default:
        language = "en-gb";
        break;
    }

    this.calendarOptions = {
      ...this.calendarOptions,
      locale: language,
      firstDay: 0,
      customButtons: {
        myTodayButton: {
          text: todayButtonText,
          click: () => {
            let calendarApi = this.fullcalendarComponent.getApi();
            calendarApi.today();
            calendarApi.changeView("timeGridDay");
          },
        },
        toggleResourceView: {
          text: toggleResourceView,
          click: this.getCustomButtons().toggleResourceView.click,
        },
      },
      views: {
        resourceTimeGridThreeDay: {
          type: "resourceTimeGrid",
          duration: { days: 3 },
          buttonText: this.translate.instant("appointments.gui.calendar.3day"),
        },
        resourceTimeGridDay: {
          type: "resourceTimeGrid",
          duration: { days: 1 },
          buttonText: this.translate.instant("appointments.gui.calendar.dayView"),
        },
      },
    };
  }

  goToAppointmentDate(event) {
    this.showCalendar = true;
    this.page = "Calendar";
    this.showPage$.next(this.page);

    setTimeout(() => {
      this.ls.doctor = event["DocUID"];
      this.ls.room = event["RID"];
      if (this.fullcalendarComponent) {
        this.goToDate(event["aptTime"]);
        this.patientsService.setPat(new Patient(event.ID, event.First, event.Last));
        this.patientsService.currentPatient.PID = event.PID;
        this.patientsService.currentPatient.Mobile = event.Mobile;

        // Manually mark the view for check after the updates
      } else {
        console.error("Calendar component is not initialized");
      }
    }, 100);
  }

  setCalendarResources() {
    if (this.appointmentsByRooms) {
      this.calenderFormattedResources = (this.ls.room || [])
        .map((roomId: string) => {
          const room = this.ls.rooms.find((r) => r.AutoIndex.toString() === roomId);
          return room ? { id: room.AutoIndex.toString(), title: room.RoomName } : null;
        })
        .filter(Boolean);
    } else {
      const doctor = this.ls.clinicUsers.find((d) => d.UID === this.ls.doctor);
      this.calenderFormattedResources = doctor ? [{ id: doctor.UID, title: doctor.Name }] : [];
    }

    this.calendarOptions = {
      ...this.calendarOptions,
      resources: this.calenderFormattedResources,
    };
  }

  onRoomsChange(event: any) {
    const selectedRoomIds = event.value;
    this.selectedRooms = selectedRoomIds;

    this.calenderFormattedResources = selectedRoomIds
      .map((roomId: number) => {
        const room = this.ls.rooms.find((room) => room.AutoIndex === roomId);
        return room ? { id: room.AutoIndex.toString(), title: room.RoomName } : null;
      })
      .filter(Boolean);

    this.calendarOptions = {
      ...this.calendarOptions,
      resources: this.calenderFormattedResources,
    };

    const calendarApi = this.fullcalendarComponent.getApi();
    const currentView = calendarApi.view.type;
    
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.showPrintIcon = currentView === "resourceTimeGridDay" && this.selectedRooms.length > 0;
    });

    this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
  }

  onDoctorsChange(event: any) {
    const selectedDoctorsIds = event.value;
    this.selectedDoctors = selectedDoctorsIds;

    this.calenderFormattedResources = selectedDoctorsIds
      .map((docId: string) => {
        const doctor = this.ls.clinicUsers.find((d) => d.UID === docId);
        return doctor ? { id: doctor.UID.toString(), title: doctor.Name } : null;
      })
      .filter(Boolean);

    this.calendarOptions = {
      ...this.calendarOptions,
      resources: this.calenderFormattedResources,
    };

    const calendarApi = this.fullcalendarComponent.getApi();
    const currentView = calendarApi.view.type;
    
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.showPrintIcon = currentView === "resourceTimeGridDay" && this.selectedDoctors.length > 0;
    });

    this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);
  }

  getCustomButtons(): any {
    let todayButtonText = this.translate.instant("appointments.gui.calendar.today");
    let toggleResourceView = this.isMultiSelect
      ? this.translate.instant("appointments.gui.calendar.defaultView")
      : this.translate.instant(
          this.ls?.premissions?.ApptsByRoom
            ? "appointments.gui.calendar.multiSelect.room"
            : "appointments.gui.calendar.multiSelect.doctor"
        );
    return {
      myTodayButton: {
        text: todayButtonText,
        click: () => {
          const calendarApi = this.fullcalendarComponent.getApi();
          calendarApi.today();
          calendarApi.changeView("timeGridDay");
        },
      },
      multiTodayButton: {
        text: todayButtonText,
        click: () => {
          const calendarApi = this.fullcalendarComponent.getApi();
          calendarApi.today();
          calendarApi.changeView("resourceTimeGridDay");
        },
      },
      toggleResourceView: {
        text: toggleResourceView,
        click: () => {
          const calendarApi = this.fullcalendarComponent.getApi();
          const currentView = calendarApi.view.type;
          const newView = currentView.startsWith("resource") ? "timeGridDay" : "resourceTimeGridDay";

          calendarApi.changeView(newView);

          this.isMultiSelect = !this.isMultiSelect;

          if (this.isMultiSelect) {
            const defaultGroup = this.settings.multiSelectGroups.find((g) => g.isDefault);

            if (defaultGroup) {
              if (this.appointmentsByRooms) {
                const roomIds = defaultGroup.RIDs?.split(",").map(Number) || [];

                setTimeout(() => {
                  this.selectedRooms = roomIds;
                  this.onRoomsChange({ value: roomIds });
                });
              } else {
                const docIds = defaultGroup.DocUIDs?.split(",").map(Number) || [];

                setTimeout(() => {
                  this.selectedDoctors = docIds;
                  this.onDoctorsChange({ value: docIds });
                });
              }

              const calendarApi = this.fullcalendarComponent?.getApi?.();
              if (calendarApi) {
                calendarApi.changeView("resourceTimeGridDay");
              }
            }
          }

          // Reset selection:
          if (this.appointmentsByRooms) {
            this.selectedRooms = [];
            this.ls.room = null;
          } else {
            this.selectedDoctors = [];
            this.ls.doctor = null;
          }

          this.setCalendarResources();

          // Multi Room/Doctor Selection:
          if (this.isMultiSelect) {
            if (this.appointmentsByRooms) {
              this.refetchRoomsActivityHours();
            } else {
              this.refetchActivityHours();
            }
            this.refetchEvents(this.startDateDisplayWithGap, this.endDateDisplayWithGap);

            this.calendarOptions = {
              ...this.calendarOptions,
              headerToolbar: {
                left:
                  this.ls.premissions && this.ls.premissions?.multiSelect ? "toggleResourceView" : "",
                center: "title",
                right: "prev resourceTimeGridDay resourceTimeGridThreeDay next multiTodayButton",
              },
              views: {
                resourceTimeGridThreeDay: {
                  type: "resourceTimeGrid",
                  duration: { days: 3 },
                  buttonText: this.translate.instant("appointments.gui.calendar.3day"),
                },
                resourceTimeGridDay: {
                  type: "resourceTimeGrid",
                  buttonText: this.translate.instant("appointments.gui.calendar.dayView"),
                },
              },
            };
          } else {
            // Default Selection:
            this.calendarOptions = {
              ...this.calendarOptions,
              headerToolbar: {
                left:
                  this.ls.premissions && this.ls.premissions?.multiSelect ? "toggleResourceView" : "",
                center: "title",
                right: "prev timeGridDay timeGridWeek dayGridMonth next myTodayButton",
              },
            };
          }

          this.calendarOptions = {
            ...this.calendarOptions,
            customButtons: this.getCustomButtons(),
          };

          calendarApi.setOption("headerToolbar", this.calendarOptions.headerToolbar);
        },
      },
    };
  }

  onMatCalendarDateSelected(momentDate: any): void {
    if (!momentDate || !momentDate.toDate) {
      console.error("Invalid date from mat-calendar:", momentDate);
      return;
    }

    const date: Date = momentDate.toDate();
    const calendarApi = this.fullcalendarComponent?.getApi();

    if (calendarApi) {
      if (this.isMultiSelect) {
        const currentView = calendarApi.view.type;
        if (currentView === "resourceTimeGridDay") {
          calendarApi.changeView("resourceTimeGridDay");
        } else {
          calendarApi.changeView("resourceTimeGridThreeDay");
        }
        calendarApi.gotoDate(date);
      } else {
        calendarApi.gotoDate(date);
        calendarApi.changeView("timeGridDay");
      }
    } else {
      console.error("FullCalendar API is not initialized.");
    }
  }

  disableHiddenDays = (date: Date | string): boolean => {
    const hiddenDays = this.calendarOptions.hiddenDays || [];
    const d = new Date(date);
    return !hiddenDays.includes(d.getDay());
  };

  getDefaultAppointmentCounts(): { [key: string]: number } {
    const counts: { [key: string]: number } = {};

    const start = new Date(this.startDateDisplay);
    const end = new Date(this.endDateDisplay);

    for (const event of this.events) {
      const eventDate = new Date(event.aptTime);
      if (eventDate < start || eventDate > end) continue;

      const key = this.appointmentsByRooms ? event.RID : event.DocUID;
      if (!key) continue;

      if (!counts[key]) {
        counts[key] = 0;
      }

      counts[key]++;
    }

    return counts;
  }

  getDefaultResources() {
    if (this.appointmentsByRooms) {
      if (this.ls.room) {
        const match = this.ls.rooms.find((r) => r.AutoIndex === Number(this.ls.room));
        return match ? [match] : [];
      }
      return this.ls.rooms;
    } else {
      if (this.ls.doctor) {
        const match = this.ls.clinicUsers.find((d) => d.UID === Number(this.ls.doctor));
        return match ? [match] : [];
      }
      return this.ls.clinicUsers;
    }
  }

  getMultiSelectCounts(): { [key: string]: number } {
    const counts: { [key: string]: number } = {};

    const start = new Date(this.startDateDisplay);
    const end = new Date(this.endDateDisplay);

    for (const event of this.events) {
      const eventDate = new Date(event.aptTime);
      if (eventDate < start || eventDate > end) continue;

      const key = this.appointmentsByRooms ? event.RID : event.DocUID;
      if (!key) continue;

      if (!counts[key]) {
        counts[key] = 0;
      }

      counts[key]++;
    }

    return counts;
  }

  getMultiSelectedResources() {
    if (this.appointmentsByRooms) {
      return this.selectedRooms
        .map((id) => this.ls.rooms.find((r) => r.AutoIndex === id))
        .filter(Boolean);
    } else {
      return this.selectedDoctors
        .map((uid) => this.ls.clinicUsers.find((d) => d.UID === uid))
        .filter(Boolean);
    }
  }

  ngAfterViewInit() {
    const calendarContainer = document.querySelector(".calendar-container") as HTMLElement | null;
    if (!calendarContainer) {
      this.setCalendarLineHeight();
      return;
    }

    // Mousemove: keep your yTime logic + make tooltip follow the cursor
    calendarContainer.addEventListener("mousemove", (event: MouseEvent) => {
      // If hovering a half-hour slot: compute yTime
      const targetSlot = (event.target as HTMLElement).closest(
        ".fc-timegrid-slot"
      ) as HTMLElement | null;
      const targetEventBg = (event.target as HTMLElement).closest(
        ".fc-timegrid-bg-harness"
      ) as HTMLElement | null;

      if (targetSlot) {
        const slotStartTime = targetSlot.getAttribute("data-time");
        if (slotStartTime) {
          const [slotHour, slotMinute] = slotStartTime.split(":").map(Number);
          const pxPerMinute = this.getpxPerMinute(targetSlot);
          if (pxPerMinute) {
            const offsetMinutes = Math.round(event.offsetY / pxPerMinute / 5) * 5;
            const baseTime = new Date();
            baseTime.setHours(slotHour, slotMinute + offsetMinutes, 0, 0);
            this.yTime = baseTime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });

            const hoverEl = this.timeHoverRef?.nativeElement;
            const containerRect = calendarContainer.getBoundingClientRect();
            if (hoverEl) {
              hoverEl.style.top = `${
                event.clientY - containerRect.top + calendarContainer.scrollTop - 15
              }px`;
              hoverEl.style.left = `${event.clientX - containerRect.left + 15}px`;
              hoverEl.style.display = "block";
            }
          }
        }
      }
      // If hovering the event background area: compute yTime relative to slots table
      else if (targetEventBg) {
        const slotsTable = calendarContainer.querySelector(
          ".fc-timegrid-slots table"
        ) as HTMLElement | null;
        if (slotsTable) {
          const tableRect = slotsTable.getBoundingClientRect();
          const mouseYRelativeToTable = event.clientY - tableRect.top;

          const cells = Array.from(slotsTable.querySelectorAll("td[data-time]"));
          let cumulativeHeight = 0;
          let targetCell: HTMLElement | null = null;
          let i = 0;

          for (const cell of cells) {
            // skip the first td in each row (your original logic)
            if (i++ % 2 === 0) continue;
            const el = cell as HTMLElement;
            cumulativeHeight += el.offsetHeight;
            if (mouseYRelativeToTable <= cumulativeHeight) {
              targetCell = el;
              break;
            }
          }

          if (targetCell) {
            const rowStartTime = targetCell.getAttribute("data-time");
            if (rowStartTime) {
              const [rowHour, rowMinute] = rowStartTime.split(":").map(Number);
              const rowHeight = targetCell.offsetHeight;
              const mouseYWithinRow = mouseYRelativeToTable - (cumulativeHeight - rowHeight);
              const minutesFromRowStart = Math.round(((mouseYWithinRow / rowHeight) * 30) / 5) * 5;

              const baseTime = new Date();
              baseTime.setHours(rowHour, rowMinute + minutesFromRowStart, 0, 0);
              this.yTime = baseTime.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });

              const hoverEl = this.timeHoverRef?.nativeElement;
              const calendarRect = calendarContainer.getBoundingClientRect();
              if (hoverEl) {
                hoverEl.style.top = `${
                  event.clientY - calendarRect.top + calendarContainer.scrollTop - 15
                }px`;
                hoverEl.style.left = `${event.clientX - calendarRect.left + 15}px`;
                hoverEl.style.display = "block";
              }
            }
          }
        }
      }

      // If tooltip is visible, keep it glued to the cursor (viewport coords)
      if (this.eventHover) {
        this.tooltipPosition = this.calcTooltipPosition(event.clientX, event.clientY);
      }
    });

    // Leave the calendar area: hide yTime + tooltip
    calendarContainer.addEventListener("mouseleave", () => {
      this.yTime = "";
      const hoverEl = this.timeHoverRef?.nativeElement;
      if (hoverEl) hoverEl.style.display = "none";

      this.eventHover = false;
      this.tooltipText = "";
      this.currentHoverId = undefined;
    });

    // Scroll: hide tooltip so it never looks detached from content
    calendarContainer.addEventListener("scroll", () => {
      this.eventHover = false;
      this.tooltipText = "";
      this.currentHoverId = undefined;
    });

    // Mouse down (drag/select/etc): hide tooltip to avoid stuck states
    calendarContainer.addEventListener("mousedown", () => {
      this.eventHover = false;
      this.tooltipText = "";
      this.currentHoverId = undefined;
    });

    // Keep your existing slot-height tweak
    this.setCalendarLineHeight();
  }

  handleTransferIconClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target?.closest) return;

    const icon = target.closest("[data-transfer-date]") as HTMLElement | null;
    if (!icon || !icon.closest(".calendar-container")) return;

    event.preventDefault();
    event.stopPropagation();

    this.openTransferAppointments(
      icon.getAttribute("data-transfer-date"),
      icon.getAttribute("data-transfer-resource")
    );
  };

  openTransferAppointments(dateStr: string, resourceId?: string) {
    const appointmentsOfDay = this.appointmentsFor(dateStr, resourceId);
    if (!appointmentsOfDay.length) return;

    this.transferSourceDate = moment(dateStr, "YYYY-MM-DD").toDate();
    this.transferSourceAppointments = appointmentsOfDay;
    this.transferResourceLabel = this.getResourceLabel(resourceId);
    this.transferResourceId = resourceId ?? (this.appointmentsByRooms ? this.ls.room : this.ls.doctor);
    this.showTransferAppointments = true;
  }

  getResourceLabel(resourceId?: string): string {
    const id = resourceId ?? (this.appointmentsByRooms ? this.ls.room : this.ls.doctor);
    if (!id) return "";

    if (this.appointmentsByRooms) {
      const room = this.ls.rooms?.find((r) => String(r.AutoIndex) === String(id));
      return room ? this.ls.roomLabel(room) : "";
    }

    const doctor = this.ls.clinicUsers?.find((d) => String(d.UID) === String(id));
    return doctor ? doctor.Name : "";
  }

  closeTransferAppointments() {
    this.showTransferAppointments = false;
    this.transferSourceDate = null;
    this.transferSourceAppointments = [];
    this.transferResourceLabel = "";
    this.transferResourceId = null;
  }

  handleAppointmentsTransferred(target: { date: Date; resourceId: number | null }) {
    this.closeTransferAppointments();

    if (target.resourceId != null) {
      this.switchToResource(target.resourceId);
    }

    this.onMatCalendarDateSelected(moment(target.date));
  }

  switchToResource(resourceId: number) {
    if (this.isMultiSelect) {
      const selected = this.appointmentsByRooms ? this.selectedRooms : this.selectedDoctors;
      const alreadyShown = selected.some((id) => String(id) === String(resourceId));
      const value: any = selected.length && typeof selected[0] === "string" ? String(resourceId) : resourceId;
      const next: any = alreadyShown ? selected : [...selected, value];

      this.appointmentsByRooms ? this.onRoomsChange({ value: next }) : this.onDoctorsChange({ value: next });
      return;
    }

    if (this.appointmentsByRooms) {
      this.ls.room = String(resourceId);
      this.refetchRoomsActivityHours();
    } else {
      this.ls.doctor = String(resourceId);
      this.refetchActivityHours();
    }
    this.handleViewChange();
  }

  fiveMinutes(add: boolean) {
    if (add) {
      this.selectedDateInfo.start = new Date(this.selectedDateInfo.start.getTime() + 5 * 60000);
      this.selectedDateInfo.end = new Date(this.selectedDateInfo.end.getTime() + 5 * 60000);
    } else {
      this.selectedDateInfo.start = new Date(this.selectedDateInfo.start.getTime() - 5 * 60000);
      this.selectedDateInfo.end = new Date(this.selectedDateInfo.end.getTime() - 5 * 60000);
    }
  }

  // adjust time slot height based on the timeSlotDuration values:
  setCalendarLineHeight() {
    try{
      const host = this.fullcalendarEl.nativeElement;
      if (!host) return;    

      if (this.ls.premissions?.calendarTimeSlotDuration === "01:00:00") {
        host.style.setProperty("--fc-slot-height", "3em");
      } else {
        host.style.setProperty("--fc-slot-height", "1.2em");
      }
    }
    catch{
      setTimeout(() => this.setCalendarLineHeight(), 2000); // Retry after 2 seconds if the host is not available
    }
  }

  checklmp() {
    if (this.lmpDate) {
      let dt = new Date(this.lmpDate);
      if (dt < this.minLMP || dt > this.maxLMP) this.invalidlmp = "is-invalid";
      else this.invalidlmp = "";
    }
  }

  sendSMS(
    autoIndexOverride?: number,
    pidOverride?: number,
    isUpdate: boolean = false,
    opts?: { onFinally?: () => void }
  ) {
    if (this.smsDisabled) {
      opts?.onFinally?.();
      return;
    }
    this.sent = false;
    this.error = "";
    this.creatingPending = true;
    const autoIndex = autoIndexOverride ?? this.appointmentService.AutoIndex;
    const pid = pidOverride ?? this.patientsService.getPat()?.PID;

    if (!autoIndex || !pid) {
      this.error = "Missing required IDs (AutoIndex / PID).";
      opts?.onFinally?.();
      return;
    }

    const body = { PID: Number(pid), immediate: true, update: isUpdate };

    this.appointmentService.sendAppointmentSms(Number(autoIndex), body).subscribe(
      (res: any) => {
        const ok = res?.ok === true || res?.status === "ok";
        if (!ok) {
          this.error = res?.error || "Failed to send SMS";
          opts?.onFinally?.();
          this.creatingPending = false;
          return;
        }
        this.sent = true;
        this.creatingPending = false;
        this.ls.linksent = (this.ls.linksent || 0) + 1;
        opts?.onFinally?.(); // clear pending before closing
        setTimeout(() => {
          this.askSendSMS = false;
          this.sent = false;
        }, 2000);
      },
      (err) => {
        this.creatingPending = false;
        this.error = err?.error?.error || err?.message || "Failed to send SMS";
        opts?.onFinally?.();
      }
    );
  }

  private safe(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  navToLastAppts() {
    const appointment = this.appointmentService.appointmentToNavBack;
    this.goToAppointmentDate(this.appointmentService.appointmentToNavBack);

    if (appointment) {
      this.setPage("Calendar", false);
      this.showAppointment = true;
      this.selectedEventInfo = appointment;
      
   
      
    }
  }
  toggleSidebar() {
    this.collapsed = !this.collapsed;
  
    setTimeout(() => {
      const calendarApi = this.fullcalendarComponent?.getApi();
      calendarApi?.updateSize();
    }, 300);
  }
}
